import React, { useState, useEffect, useContext } from 'react';
import {
  Select,
  MenuItem,
  TextField,
  Box,
  SelectChangeEvent,
} from '@mui/material';
import { track } from '@amplitude/analytics-browser';
import { authContext } from '@staffedupusa/shared-components';

interface Setting {
  label: string;
  event: string;
  customEvent?: string;
  customInputEvent?: string;
  value: string | number | boolean;
  field: string;
  action: string;
  min: number;
  max: number;
  showDropDown: boolean;
  tooltip: string;
  reminder?: {
    label: string;
    event: string;
    value: string | number;
    field: string;
    tooltip: string;
    min: number;
    max: number;
  };
}

interface AutoApplicationSelectionsProps {
  setting: Setting;
  showReminder: boolean;
  updateProfile: (data: Record<string, string | number>) => void;
  simpleItems: Record<string, string | number>;
  reminderItems?: Record<string, string | number>;
}

const AutoApplicationSelections: React.FC<AutoApplicationSelectionsProps> = ({
  setting,
  showReminder,
  updateProfile,
  reminderItems,
  simpleItems,
}) => {
  const { authData } = useContext(authContext);
  const isCompany = authData?.user?.company;
  const [selectedValue, setSelectedValue] = useState<
    string | number | 'custom'
  >(
    showReminder &&
      setting.reminder &&
      (setting.reminder.value === 0 ||
        (reminderItems &&
          Object.values(reminderItems).includes(setting.reminder.value)))
      ? setting.reminder.value
      : setting.value &&
          !showReminder &&
          typeof setting.value !== 'boolean' &&
          Object.values(simpleItems).includes(setting.value)
        ? setting.value
        : 'custom',
  );

  const [isCustom, setIsCustom] = useState(false);
  const [customInput, setCustomInput] = useState<string | number>(
    selectedValue === 'custom' && showReminder && setting.reminder
      ? setting.reminder.value
      : selectedValue === 'custom' &&
          !showReminder &&
          typeof setting.value !== 'boolean'
        ? setting.value
        : '',
  );
  const [options, setOptions] = useState<
    { label: string; value: string | number }[]
  >([]);
  useEffect(() => {
    const initialOptions = Object.entries(
      showReminder && reminderItems ? reminderItems : simpleItems,
    ).map(([label, value]) => ({ label, value }));

    setOptions(initialOptions);

    // Check if the initial value is part of the predefined options
    const isPredefined = initialOptions.some(
      (option) => option.value === selectedValue,
    );
    setIsCustom(!isPredefined && selectedValue === 'custom');
  }, [showReminder, reminderItems, simpleItems]);

  const handleSelectChange = (event: SelectChangeEvent<string | number>) => {
    const value = event.target.value as string | number;

    if (value === 'custom') {
      setIsCustom(true);
      setSelectedValue('custom');
    } else {
      setIsCustom(false);
      setSelectedValue(value);
      setCustomInput('');
      updateProfile({
        [reminderItems && setting.reminder?.field
          ? setting.reminder.field
          : setting.field]: value,
      });
    }
  };

  const handleCustomInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setCustomInput(value);
    if (/^-?\d+$/.test(value)) {
      // setSelectedValue('custom');
      updateProfile({
        [reminderItems && setting.reminder?.field
          ? setting.reminder.field
          : setting.field]: Number(value),
      });
    }
  };
  return (
    <Box sx={{ width: 120 }}>
      <Select
        value={isCustom ? 'custom' : selectedValue}
        onChange={handleSelectChange}
        onClick={() => {
          isCompany &&
            track(
              isCustom && setting.customEvent
                ? setting.customEvent
                : showReminder
                  ? setting.reminder?.event ?? ''
                  : setting.event ?? '',
            );
        }}
        displayEmpty
        fullWidth
        size="small"
        sx={{ minWidth: 100 }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
        {!showReminder && (
          <MenuItem value="custom" sx={{ fontStyle: 'italic' }}>
            Custom
          </MenuItem>
        )}
      </Select>

      {isCustom && !showReminder && (
        <TextField
          fullWidth
          size="small"
          type="number"
          sx={{ minWidth: 100, mt: 2 }}
          value={customInput}
          onClick={() => {
            if (setting.customInputEvent) track(setting.customInputEvent);
          }}
          onChange={handleCustomInputChange}
          placeholder="Enter days"
          error={customInput !== '' && !/^-?\d+$/.test(String(customInput))}
          helperText={
            customInput !== '' && !/^-?\d+$/.test(String(customInput))
              ? 'Please enter a valid integer'
              : ''
          }
        />
      )}
    </Box>
  );
};

export default AutoApplicationSelections;
